import { Add, ArrowBack } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import { ContactType } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import {
	BoundAsyncSelect,
	BoundTextField,
	BoundNumberField,
	useAsyncSelectContactQuery,
	useAsyncSelectClientProjectsQuery,
	useAsyncSelectMembersWithEmailQuery,
} from "@repo/shared/components";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import InvoiceItemComponent from "../components/InvoiceItem";
import { InvoiceSummary } from "../components/InvoiceSummary";
import { useOfferValidator } from "../hooks/OfferValidationHooks";
import { useUpdateOfferHook } from "../hooks/UpdateOfferHook";
import { useCreateOfferHook } from "../hooks/CreateOfferHook";

import type { Offer, InvoiceItem } from "../types/CommercialDocumentTypes";
import type { RuleFactoryType } from "@repo/server-api/types";
import type { SetStateAction } from "react";

export type OfferEditProps = {
	offer: Offer;
	setOffer: (value: SetStateAction<Offer>) => void;
	rulefactory?: RuleFactoryType;
};

const OfferEdit = (props: OfferEditProps) => {
	const { t } = useTranslation();

	const navigate = useNavigate();

	const { offer, setOffer } = props;
	const { errors, setErrors, validate } = useOfferValidator();

	const useBinding = <TValue,>(selector: (m: Offer) => TValue) => {
		return useBoundDeep(offer, setOffer, selector, errors, setErrors);
	};

	const offerClientField = useBinding((i) => i.client ?? null);
	const offerContractorField = useBinding((i) => i.contractor ?? null);
	const offerProjectField = useBinding((i) => i.project ?? null);
	const offerClientContactPersonField = useBinding((i) => i.contactPerson ?? null);

	const searchContractorHook = useAsyncSelectContactQuery([ContactType.Contractor]);
	const searchClientHook = useAsyncSelectContactQuery([ContactType.Client]);
	const searchProjectHook = useAsyncSelectClientProjectsQuery(offer.client?.id ?? null);
	const searchClientContactPersonHook = useAsyncSelectMembersWithEmailQuery(
		offer.client?.id ?? null,
		[ContactType.ContactPerson],
	);

	const offerHeaderNoteField = useBinding((i) => i.headerNote);
	const offerFooterNoteField = useBinding((i) => i.footerNote);

	const validityDaysField = useBinding((i) => i.paymentDeadlineDays);

	const { updateOffer, loading } = useUpdateOfferHook({
		onSaved: (updatedOffer) => {
			setOffer(updatedOffer);
		},
	});

	const { save: createOffer, loading: creating } = useCreateOfferHook({
		onSaved: (newOfferId: string) => {
			setOffer((prev) => ({ ...prev, id: newOfferId }));
			navigate(`/offers/edit/${encodeURIComponent(newOfferId)}`, { replace: true });
		},
	});

	// TODO: Remove this when you find better solution
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		// Clear contact persons and project if the selected client change
		if (!loaded) {
			setLoaded(true);
			return;
		}
		offerProjectField.onChangeValue(null);
		searchProjectHook.resetSearch();
		offerClientContactPersonField.onChangeValue(null);
		searchClientContactPersonHook.resetSearch();
	}, [offerClientField.value?.id]);

	const handleSubmit = async () => {
		// const { isValid } = await validate(offer);
		await validate(offer);

		if (loading || creating) {
			return;
		}

		if (offer.id) updateOffer(offer);
		else createOffer(offer);
	};

	const handleCancel = () => {
		navigate(-1);
	};

	return (
		<>
			<Box
				style={{
					minHeight: "90vh",
					height: "auto",
					width: "100%",
					position: "relative",
				}}>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBack />
				</Button>
				<div className="left purpleHeader" style={{ marginLeft: 20 }}>
					<span className="left">{offer.id ? t("offer-edit") : t("create-offer")}</span>
				</div>
				<div className="clear" />
				<div className="w-100 paddingBottom40" style={{ marginTop: "20px" }}>
					<div className="hrTitle lightGreyFont paddingBottom40">
						<span className="left">{t("global-information").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter">
						<BoundAsyncSelect
							label={t("clients")}
							boundedValue={offerClientField}
							searchHook={searchClientHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("project")}
							boundedValue={offerProjectField}
							searchHook={searchProjectHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("client-contacts")}
							boundedValue={offerClientContactPersonField}
							searchHook={searchClientContactPersonHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundAsyncSelect
							label={t("contractors")}
							boundedValue={offerContractorField}
							searchHook={searchContractorHook}
							additionalProps={{ className: "w-100 left" }}
						/>
					</Box>
					<Box className="fullCenter" sx={{ mt: 2.5 }}>
						<BoundNumberField
							label={t("validity-days")}
							boundedValue={validityDaysField}
							additionalProps={{ className: "w-20 left" }}
						/>
					</Box>
					<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
						<span className="left">{t("offer-header").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter">
						<BoundTextField
							label={t("header-note")}
							boundedValue={offerHeaderNoteField}
							additionalProps={{
								className: "w-100 left",
								rows: 4,
								multiline: true,
								placeholder: t("offer-header-note-placeholder"),
								focused: true,
							}}
						/>
					</Box>
					<div className="clear" />
					<InvoiceItemComponent
						invoice={offer}
						setInvoice={setOffer}
						errors={errors}
						setErrors={setErrors}
					/>
					<div className="clear" />
					<Button
						className="greenButton"
						variant="contained"
						data-testid="offer-add-item-button"
						onClick={() => {
							const newItem = {
								id: crypto.randomUUID(),
								name: "",
								description: "",
								quantity: 1,
								unit: "",
								unitPrice: 0,
								discount: 0,
								totalPrice: 0,
								tax: 0.19,
								taxCode: "U19",
							};
							setOffer((prevOffer) => ({
								...prevOffer,
								items: [...(prevOffer.items || []), newItem as unknown as InvoiceItem],
							}));
							setErrors((prevErrors) => ({
								...prevErrors,
								items: "",
							}));
						}}>
						{t("article")} <Add />
					</Button>
					<InvoiceSummary items={offer.items} />
					<div className="clear" />
					<div className="hrTitle lightGreyFont paddingBottom40" style={{ marginTop: 20 }}>
						<span className="left">{t("offer-footer").toLocaleUpperCase()}</span>
						<hr className="right" />
					</div>
					<Box className="fullCenter">
						<BoundTextField
							label={t("footer-note")}
							boundedValue={offerFooterNoteField}
							additionalProps={{ className: "w-100", rows: 4, multiline: true }}
						/>
					</Box>
				</div>
				<Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 3, mb: 2, pr: "30px" }}>
					<LoadingButton
						loading={loading || creating}
						className={loading || creating ? "" : "blueButton"}
						color="secondary"
						variant="contained"
						data-testid="offer-save-button"
						onClick={handleSubmit}>
						{t("save")}
					</LoadingButton>
				</Box>
			</Box>
		</>
	);
};

export default OfferEdit;
